// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Faculty.js

import React from 'react';
import { FiChevronRight, FiMail, FiBookOpen, FiAward } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

// Helper functions
const getName = (m) => {
  if (!m) return 'Unknown';
  if (m.fullName) return m.fullName;
  if (m.name) return typeof m.name === 'string' ? m.name : `${m.name.first || ''} ${m.name.last || ''}`.trim();
  if (m.firstName || m.lastName) return `${m.firstName || ''} ${m.lastName || ''}`.trim();
  if (m._id) return String(m._id);
  if (m.email) return m.email;
  return 'Unknown';
};

const getImage = (m, apiBaseURL) => {
  const candidate = m.image || m.photo || m.imageUrl || m.avatar || m.picture || (m.media && m.media[0] && m.media[0].url) || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

const getDesignation = (m) => m.designation || m.role || m.title || m.position || '';

// Faculty Card Component
const FacultyCard = ({ member, apiBaseURL, onViewProfile }) => {
  const { primary, secondary, primaryLight, secondaryLight, fontBody, fontHeading } = useBrandColors();
  const img = getImage(member, apiBaseURL);
  const name = getName(member);
  const desig = getDesignation(member);
  const shortBio = member.experience || member.bio || member.summary || member.description || '';

  // Calculate experience safely
  const experienceYears = member.joinDate
    ? new Date().getFullYear() - new Date(member.joinDate).getFullYear()
    : null;

  // Get initials for fallback
  const getInitials = () => {
    if (name === 'Unknown') return 'F';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div
      className="group bg-white rounded-3xl overflow-hidden border border-gray-100 transition-all duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.04)] hover:-translate-y-1.5 flex flex-col h-full"
      style={{ fontFamily: fontBody }}
    >
      {/* Visual Header / Profile Photo Box */}
      <div 
        className="relative aspect-[11/9] overflow-hidden w-full bg-gray-50"
        style={{ backgroundColor: primaryLight || '#F9FAFB' }}
      >
        {img ? (
          <img
            src={img}
            alt={name}
            className="w-full h-full object-cover transform transition-transform duration-1000 ease-out group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${primaryLight || '#F3F4F6'}, ${secondaryLight || '#E5E7EB'})` }}
          >
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-white shadow-sm border border-gray-100">
              <span className="text-xl font-bold tracking-tight" style={{ color: primary }}>
                {getInitials()}
              </span>
            </div>
          </div>
        )}

        {/* Dynamic Static Status Badges Overlay */}
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          {experienceYears && (
            <div className="flex items-center gap-1 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-white text-[10px] font-semibold tracking-wide uppercase">
              <FiAward className="w-3 h-3 text-amber-400" />
              {experienceYears}+ Yrs Exp
            </div>
          )}
          {member.publications > 0 && (
            <div className="flex items-center gap-1 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-white text-[10px] font-semibold tracking-wide uppercase">
              <FiBookOpen className="w-3 h-3 text-sky-400" />
              {member.publications} Pubs
            </div>
          )}
        </div>
      </div>

      {/* Content Space */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="mb-3">
          <h4 
            className="text-lg font-bold text-gray-900 tracking-tight leading-snug group-hover:text-opacity-80 transition-colors"
            style={{ fontFamily: fontHeading }}
          >
            {name}
          </h4>
          <p className="text-xs font-semibold uppercase tracking-wider mt-0.5" style={{ color: secondary || primary }}>
            {desig}
          </p>
        </div>

        {shortBio && (
          <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed mb-4 font-normal">
            {shortBio}
          </p>
        )}

        {/* Qualifications Badges */}
        {member.qualifications && Array.isArray(member.qualifications) && member.qualifications.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-5">
            {member.qualifications.slice(0, 2).map((qual, idx) => (
              <span
                key={idx}
                className="text-[10px] font-medium px-2.5 py-1 rounded-full text-gray-600 border border-gray-100"
                style={{ backgroundColor: `${primary}04` }}
              >
                {qual}
              </span>
            ))}
            {member.qualifications.length > 2 && (
              <span className="text-[10px] font-medium px-2 py-1 rounded-full text-gray-400 bg-transparent border border-dashed border-gray-200">
                +{member.qualifications.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Footer Meta / Dynamic Action button */}
        <div className="mt-auto space-y-4">
          {member.email && (
            <div className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors">
              <FiMail className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="text-xs truncate font-medium">{member.email}</span>
            </div>
          )}

          <button
            onClick={() => onViewProfile(member)}
            className="w-full py-2.5 px-4 rounded-xl font-medium text-xs transition-all duration-300 flex items-center justify-center gap-1.5 shadow-sm group/btn border"
            style={{ 
              backgroundColor: primary,
              borderColor: primary,
              color: '#ffffff',
            }}
            type="button"
          >
            <span>View Profile</span>
            <FiChevronRight className="w-3.5 h-3.5 transform transition-transform duration-300 group-hover/btn:translate-x-0.5" />
          </button>
        </div>

        {/* Debug block layout fallback */}
        {(!member || (!member.firstName && !member.name && !member.fullName)) && (
          <pre className="mt-4 text-[10px] text-gray-400 max-h-20 overflow-auto bg-gray-50 p-2 rounded-lg">
            {JSON.stringify(member || {}, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

// Main Faculty Component
const Faculty = ({
  facultyByDepartment = {},
  facultyList = [],
  onViewProfile = () => {},
  apiBaseURL = '',
  sectionTitle,
  sectionSubtitle
}) => {
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  
  const groups = Object.keys(facultyByDepartment || {}).length
    ? facultyByDepartment
    : (Array.isArray(facultyList) && facultyList.length ? { Faculty: facultyList } : {});

  if (!Object.keys(groups).length) return null;

  const title = sectionTitle || 'Distinguished Faculty';
  const subtitle = sectionSubtitle || 'World-class educators and researchers dedicated to advancing knowledge and inspiring the next generation';

  return (
    <section id="faculty" className="py-20 md:py-28 bg-white">
      <div className="container mx-auto px-6 max-w-7xl">
        
        {/* Modern Clean Section Header Block */}
        <div className="max-w-3xl mb-16 md:mb-24">
          <span 
            className="inline-block text-xs font-bold tracking-[0.25em] uppercase mb-4"
            style={{ color: secondary || primary, fontFamily: fontBody }}
          >
            Academic Leadership
          </span>
          <h2 
            className="text-3xl md:text-5xl font-black tracking-tight mb-5 text-gray-900"
            style={{ fontFamily: fontHeading }}
          >
            {title}
          </h2>
          <p className="text-base md:text-lg text-gray-500 font-normal leading-relaxed max-w-2xl" style={{ fontFamily: fontBody }}>
            {subtitle}
          </p>
        </div>

        {/* Faculty Grid divided cleanly by Department segments */}
        {Object.entries(groups).map(([department, members]) => {
          const arr = Array.isArray(members) ? members : [];
          if (arr.length === 0) return null;

          return (
            <div key={department} className="mb-20 last:mb-0">
              {/* Department Title Segment Banner */}
              <div className="flex items-baseline gap-3 mb-10 pb-4 border-b border-gray-100">
                <h3 
                  className="text-xl md:text-2xl font-bold tracking-tight text-gray-900"
                  style={{ fontFamily: fontHeading }}
                >
                  {department}
                </h3>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  ({arr.length} {arr.length === 1 ? 'member' : 'faculty'})
                </span>
              </div>

              {/* Grid Dynamic Render Strategy Wrapper */}
              <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {arr.map((m) => (
                  <FacultyCard
                    key={String(m._id || m.id || getName(m))}
                    member={m}
                    apiBaseURL={apiBaseURL}
                    onViewProfile={onViewProfile}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Faculty;